import {Component, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {InformType} from '../../../../global/util/enum/InformType';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {EnterpriseService} from '../../../service/enterprise.service';
import {Enterprise} from '../../../model/Enterprise';
import {AuthService} from '../../../service/auth/auth.service';
import {Constant} from '../../../../global/util/Constant';
import {LocalStorage} from '../../../../global/util/LocalStorage';
import {Partner} from '../../../model/Partner';
import HttpStatusCode from '../../../../global/util/HttpStatusCode';
import {StringUtil} from '../../../../global/util/StringUtil';
import {Dialog} from '../../../../global/util/Dialog';
import {TranslateService} from '@ngx-translate/core';
import {MoreAboutEnterprise} from '../../../model/common/MoreAboutEnterprise';

@Component({
  selector: 'app-add-enterprise',
  templateUrl: './add-edit-enterprise.component.html',
  styleUrls: ['./add-edit-enterprise.component.css']
})
export class AddEditEnterpriseComponent implements OnInit {
  public enterprise: Enterprise;
  public loading: boolean;
  public enterpriseForm: FormGroup;
  public ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  public ROLE_ADMIN = Constant.ROLE_ADMIN;
  public ROLE_PARTNER = Constant.ROLE_PARTNER;
  public partners: Partner[];
  public enterprises: Enterprise[];
  // public enterpriseOrigin: Enterprise;
  public logoSrc: string | ArrayBuffer;
  public previewLogoWidth = '197';
  limitStorage: number;
  storageType: number;
  storageTypes: any[] = [];

  constructor(public activeModal: NgbActiveModal,
              private formBuilder: FormBuilder,
              private enterpriseService: EnterpriseService,
              private authenticationService: AuthService,
              private translate: TranslateService) {
    this.createForm();
  }

  ngOnInit() {
    this.enterprise.documentType = '6';
    this.storageTypes = LocalStorage.getStorageTypes();
    this.loadDefaultData();
    this.setDefaultData();
    this.setDefaultStorageData();
  }

  private loadDefaultData() {
    if (!this.isNewEnterprise) {
      const logoFilename2Show = this.enterprise._more.logoFilename;
      this.enterpriseForm.get('logoFilename2Show').setValue(logoFilename2Show);
      this.loadLogoSrc(this.enterprise);
    }
    if (this.hasRole(this.ROLE_SUPERADMIN)) {
      this.partners = LocalStorage.getPartners();
    }
  }

  private setDefaultData() {
    if (this.hasRole(this.ROLE_SUPERADMIN)) {
      if (this.isNewEnterprise) {
        this.enterprise.partnerId = this.partners[Constant.ZERO_INDEX].id;
      }
    } else {
      this.enterprise.partnerId = LocalStorage.getPartnerId();
    }
  }

  public save(): void {
    if (this.enterpriseForm.invalid) {
      Object.values(this.enterpriseForm.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }
    this.setValuesBefore2Save();
    this.loading = true;
    if (this.isNewEnterprise) {
      if (this.existEnterprise) {
        this.activeModal.close({informType: InformType.Warning});
        return;
      }
      this.register();
    } else {
      this.edit();
    }
  }

  private setValuesBefore2Save() {
    this.enterprise.logoBase64Data = this.enterpriseForm.get('logoFileBase64').value;
    this.enterprise.logoFilename = this.enterpriseForm.get('logoFilename').value;
  }

  get existEnterprise(): boolean {
    if (this.enterprises) {
      const partnerId = this.hasRole(this.ROLE_SUPERADMIN) ? this.enterprise.partnerId : LocalStorage.getPartnerId();
      return this.enterprises.find(enterprise => enterprise.partnerId === partnerId && enterprise.documentNumber === this.enterprise.documentNumber) !== undefined;
    }
    return false;
  }

  public cancel(): void {
    this.activeModal.close({informType: InformType.Anything});
  }

  public register() {
    this.completeTradeName();
    this.setStorageLimitValue();
    const partnerId = this.enterprise.isPartner ? 0 : this.hasRole(this.ROLE_SUPERADMIN) ? this.enterprise.partnerId : LocalStorage.getPartnerId();
    const url = Constant.ROOT_API_V1.concat('/partners/', partnerId, '/enterprises');
    this.enterpriseService.save(url, this.enterprise).subscribe((enterprise1: any) => {
      if (this.hasRole(this.ROLE_SUPERADMIN) || this.hasRole(this.ROLE_ADMIN) || this.hasRole(this.ROLE_PARTNER)) {
        LocalStorage.updateEmbeddedValuesBy(enterprise1, 'enterprises', Constant.ADD);
        const job = enterprise1.jobs[Constant.ZERO_INDEX];
        LocalStorage.updateEmbeddedValuesBy(job, 'jobs', Constant.ADD);
      }
      this.loadProfile();
      this.loading = false;
      this.activeModal.close({informType: InformType.Success, enterprise: enterprise1});
    }, error => {
      this.loading = false;
      if (error.status === HttpStatusCode.FOUND) {
        this.activeModal.close({informType: InformType.Warning, enterprise: error.error});
      } else {
        this.activeModal.close({informType: InformType.Danger});
      }
    });
  }

  public edit() {
    this.setStorageLimitValue();
    this.enterpriseService.edit(this.enterprise).subscribe(enterprise1 => {
      LocalStorage.updateEmbeddedValuesBy(enterprise1, 'enterprises', Constant.ADD);
      this.loadProfile();
      this.loading = false;
      this.activeModal.close({informType: InformType.Success, enterprise: enterprise1});
    }, error => {
      this.loading = false;
      this.activeModal.close({informType: InformType.Warning, message: error.error.message});
    });
  }

  get isNewEnterprise(): boolean {
    return this.enterprise.id === undefined;
  }

  private createForm() {
    this.enterpriseForm = this.formBuilder.group(
      {
        partner: [''],
        docType: ['', [Validators.required]],
        docNumber: ['', Validators.compose(
          [Validators.minLength(11),
            Validators.maxLength(11),
            Validators.required])],
        name: ['', [Validators.required]],
        tradeName: [''],
        email: ['',
          Validators.pattern('^([a-zA-Z0-9_\\-\\.]+)@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.)|(([a-zA-Z0-9\\-]+\\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\\]?)$')],
        isPartner: [''],
        isCustomer: [''],
        maxWorkflows: [''],
        logoFileBase64: [''],
        logoFilename: [''],
        logoFilename2Show: [''],
        limitStorage: [''],
        storageType: ['']
      });
  }

  public isInvalidControl(ctrlName: string) {
    return this.enterpriseForm.get(ctrlName).invalid && this.enterpriseForm.get(ctrlName).touched;
  }

  private completeTradeName() {
    if (!this.enterprise.tradeName) {
      this.enterprise.tradeName = this.enterprise.name;
    }
  }

  public hasRole(roleDescription: string) {
    return this.authenticationService.hasRole(roleDescription);
  }

  changePartner(event): void {
    this.enterprise.isCustomer = event.target.checked;
    if (!event.target.checked && this.enterprise.isPartner) {
      const index = this.partners.findIndex(partner1 => partner1.id === this.enterprise.partnerId);
      this.partners.splice(index, 1);
      this.enterprise.partnerId = this.partners[Constant.ZERO_INDEX].id;
    }
  }

  changeCustomer(event): void {
    if (this.enterprise.isPartner) {
      if (!event.target.checked) {
        event.target.checked = true;
        return;
      }
    }
  }

  get createPartner(): boolean {
    return this.enterprise.isPartner;
  }

  public hasAuthority(code: string): boolean {
    return this.authenticationService.hasAuthority(code);
  }

  get defaultLogoSrc(): string | ArrayBuffer {
    return this.logoSrc || this.placeHoldItLogo;
  }

  get placeHoldItLogo(): string {
    return Constant.DEFAULT_IMAGE_PATH_235_150;
  }

  get styleDimensionsLogo(): string {
    return 'width: ' + this.previewLogoWidth + 'px; height: 100px';
  }

  public readSelectedLogo(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => this.logoSrc = reader.result;
      reader.readAsDataURL(file);
    }
  }

  public changeStatus(event: any) {
    const testFile = event[Constant.ZERO_INDEX];
    this.enterpriseForm.patchValue({
      logoFileBase64: testFile.base64,
      logoFilename: testFile.name,
      logoFilename2Show: StringUtil.cut(testFile.name, 42, this.translate.instant('MESSAGE.SELECT')),
    });
    this.updateValueAndValidityLogoFields();
  }

  private updateValueAndValidityLogoFields() {
    this.enterpriseForm.get('logoFileBase64').updateValueAndValidity();
    this.enterpriseForm.get('logoFilename').updateValueAndValidity();
    this.enterpriseForm.get('logoFilename2Show').updateValueAndValidity();
  }

  private loadLogoSrc(enterprise: Enterprise) {
    if (enterprise && enterprise._more.logoFileExtension && enterprise._more.base64LogoFile) {
      this.logoSrc = 'data:image/' + enterprise._more.logoFileExtension + ';base64,' + enterprise._more.base64LogoFile;
    } else {
      this.logoSrc = this.placeHoldItLogo;
    }
  }

  private loadProfile() {
    const personId = LocalStorage.getPersonId();
    const url = Constant.ROOT_API_V1.concat('/people/', String(personId), '/profile');
    this.enterpriseService.get(url).subscribe((value: any) => {
      this.loading = false;
      LocalStorage.setItem('profile', value.profile);
    }, () => {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.PROFILE'), this.translate.instant('MESSAGE.ERROR-LOADING-USER-PROFILE'));
    });
  }

  private setDefaultStorageData() {
    if (this.isNewEnterprise) {
      this.limitStorage = LocalStorage.getLimitStorage();
      this.storageType = this.storageTypes[1].id;
    } else {
      this.limitStorage = this.enterprise._more.limitStorage;
      this.storageType = this.enterprise._more.storageType === undefined ? this.storageTypes[1].id : this.enterprise._more.storageType;
    }
  }

  private setStorageLimitValue() {
    const more = new MoreAboutEnterprise();
    more.limitStorage = this.limitStorage;
    more.storageType = this.storageType;
    this.enterprise.moreAboutEnterprise = more;
  }

  // Obtiene datos de SUNAT por número de ruc.
  getBy(documentNumber: string) {
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/search/on-sunat/' + documentNumber + '/enterprises');
    this.enterpriseService.get(url).subscribe((enterpriseFoundded: any) => {
      this.loading = false;
      if (enterpriseFoundded) {
        this.enterprise.documentNumber = enterpriseFoundded.documentNumber;
        this.enterprise.name = enterpriseFoundded.name;
        this.enterprise.tradeName = enterpriseFoundded.tradeName;
      }
    }, error => {
      this.loading = false;
      Dialog.show(InformType.Warning, this.translate.instant('TITLE.ENTERPRISE'), error.error.message);
    });
  }

  get enableBtnSearch() {
    return this.enterprise.documentNumber !== undefined ? this.enterprise.documentNumber.length === 11 && this.isNewEnterprise : false;
  }
}
